import { inject, Injectable } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { routes } from 'app/shared/routes';
import { PERSONAL_SPACE_ROOT } from 'app/core/workspace/workspace.service';

const PERSONAL_SPACE_HOME = `${PERSONAL_SPACE_ROOT}/cra/add`;
import {
    PermissionRequirement,
    PermissionScope,
} from './permission.types';

/** Du plus large au plus restreint : le premier périmètre accordé l'emporte. */
const SCOPES_WIDEST_FIRST: PermissionScope[] = ['ALL', 'OWN'];

/**
 * Droits de l'utilisateur connecté. Le propriétaire de la société et le super admin ont tout ;
 * un collaborateur a les permissions de son rôle (lues dans le jeton) ; les autres comptes n'en ont aucune.
 * Le backend refait les mêmes contrôles : ceci ne sert qu'à adapter l'interface.
 *
 * Un aperçu (« voir l'interface comme ce rôle ») remplace temporairement les permissions par celles d'un rôle,
 * pour l'interface seulement.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
    private _authService = inject(AuthService);
    private _preview: { roleName: string; permissions: string[] } | null = null;

    /** Pages d'arrivée possibles d'un collaborateur, par ordre de préférence. */
    private readonly _collaboratorLandings: [PermissionRequirement, string][] = [
        ['CLIENT:READ', routes.listClient],
        ['REQUIREMENT:READ', '/requirements/list'],
        ['CONTACT:READ', '/contacts/list'],
        ['DASHBOARD:READ', '/dashboard'],
    ];

    can(requirement: PermissionRequirement): boolean {
        return this.scopeOf(requirement) !== null;
    }

    /** Périmètre accordé (le plus large si plusieurs), null si le droit n'est pas accordé. */
    scopeOf(requirement: PermissionRequirement): PermissionScope | null {
        const granted = this._grantedPermissions();
        if (granted === 'everything') {
            return 'ALL';
        }
        return SCOPES_WIDEST_FIRST.find((scope) => granted.includes(`${requirement}:${scope}`)) ?? null;
    }

    /** Droit sur une fiche précise : il en est responsable, ou le droit couvre toute la société. */
    canOnRecord(requirement: PermissionRequirement, ownerId: number | null | undefined): boolean {
        const scope = this.scopeOf(requirement);
        if (scope === 'ALL') {
            return true;
        }
        if (scope === null || ownerId == null) {
            return false;
        }
        const me = Number(this._authService.getAuthUserIdFromToken());
        return ownerId === me;
    }

    /** Aperçu de l'interface avec les droits d'un rôle (administration) ; le backend garde les vrais droits. */
    startPreview(roleName: string, permissions: string[]): void {
        this._preview = { roleName, permissions };
    }

    stopPreview(): void {
        this._preview = null;
    }

    get previewRoleName(): string | null {
        return this._preview?.roleName ?? null;
    }

    private _grantedPermissions(): string[] | 'everything' {
        if (this._preview) {
            return this._preview.permissions;
        }
        if (this._authService.isCompanyOwner() || this._authService.isSuperAdmin()) {
            return 'everything';
        }
        return this._authService.isManager() ? this._authService.getPermissionsFromToken() : [];
    }

    /** Page d'accueil : celle du rôle, ou la première page autorisée pour un collaborateur. */
    landingRoute(): string {
        if (!this._authService.isManager() && !this._preview) {
            return this._authService.homeRoute();
        }
        const landing = this._collaboratorLandings.find(([requirement]) => this.can(requirement));
        if (landing) {
            return landing[1];
        }
        // Rôle sans module ouvert : il lui reste son espace personnel s'il est aussi une ressource.
        return this._authService.hasPersonalSpace() && !this._preview ? PERSONAL_SPACE_HOME : '/404-not-found';
    }
}
